/**
 * EntitlementsSection Component
 *
 * Displays the user's entitlements (capabilities) with usage information.
 * Part of the "My Access" page for all users.
 */

import { AllInclusive, Security } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { EntitlementResponse } from '@store/generated/subscriptions';

interface EntitlementsSectionProps {
  entitlements: EntitlementResponse[];
  isLoading?: boolean;
}

// Get progress bar color based on usage percentage
const getUsageColor = (current: number, quota: number): 'primary' | 'warning' | 'error' => {
  const percentage = (current / quota) * 100;
  if (percentage >= 100) return 'error';
  if (percentage >= 80) return 'warning';
  return 'primary';
};

// Format usage period for display
const formatUsagePeriod = (period: string | null | undefined): string => {
  if (!period) return '';
  return period.charAt(0).toUpperCase() + period.slice(1);
};

const getDisplayUsage = (
  currentUsage: number | null | undefined,
  quota: number | null | undefined,
): number => {
  const usageValue = currentUsage ?? 0;
  if (quota === null || quota === undefined) {
    return usageValue;
  }
  return Math.min(usageValue, quota);
};

export function EntitlementsSection({ entitlements, isLoading }: EntitlementsSectionProps) {
  if (isLoading) {
    return (
      <Box component="section" sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Capabilities
        </Typography>
        <Card elevation={1}>
          <CardContent>
            {[1, 2, 3].map(i => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="circular"  sx={{ width: 24, height: 24 }}/>
                <Skeleton variant="text"  sx={{ width: "30%" }}/>
                <Skeleton variant="text"  sx={{ width: "20%" }}/>
                <Skeleton variant="rectangular"  sx={{ width: "30%", height: 8 }}/>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (entitlements.length === 0) {
    return (
      <Box component="section" sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Capabilities
        </Typography>
        <Card elevation={1}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Security sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No capabilities available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your subscription doesn&apos;t include any specific capabilities.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Capabilities
      </Typography>
      <Card elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Capability</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Quota</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Usage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entitlements.map((entitlement, index) => (
              <TableRow key={`${entitlement.capability_id}-${index}`}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Security sx={{ fontSize: 18, color: 'grey.500' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entitlement.capability_display_name || entitlement.capability_name}
                      </Typography>
                      {entitlement.capability_description && (
                        <Typography variant="caption" color="text.secondary">
                          {entitlement.capability_description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={entitlement.bundle_name} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {entitlement.quota !== null ? (
                    <Box>
                      <Typography variant="body2">{entitlement.quota}</Typography>
                      {entitlement.usage_period && (
                        <Typography variant="caption" color="text.secondary">
                          per {formatUsagePeriod(entitlement.usage_period)}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Chip
                      icon={<AllInclusive sx={{ fontSize: 14 }} />}
                      label="Unlimited"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {entitlement.quota !== null ? (
                    <Tooltip
                      title={`${getDisplayUsage(entitlement.current_usage, entitlement.quota)} used of ${entitlement.quota}${entitlement.quota_remaining !== null ? `, ${entitlement.quota_remaining} remaining` : ''}`}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {getDisplayUsage(entitlement.current_usage, entitlement.quota)} /{' '}
                          {entitlement.quota}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(
                            ((entitlement.current_usage ?? 0) / (entitlement.quota ?? 1)) * 100,
                            100,
                          )}
                          color={getUsageColor(
                            getDisplayUsage(entitlement.current_usage, entitlement.quota),
                            entitlement.quota ?? 0,
                          )}
                          sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                        />
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}
